import { getterTree } from "typed-vuex";
import { SnapshotPlacement } from "~/../types/lib/responses/user/user.res";
import state, { UserState } from "./state";

/**
 * User module getters.
 */
const getters = getterTree(state, {
    /**
     * Checks whether a user is currently logged in.
     *
     * @param {UserState} state User state
     * @return {boolean} Flag, whether a user is currently logged in or not
     */
    isLoggedIn(state: UserState): boolean {
        return !!state.token;
    },

    /**
     * Checks whether the logged in user is a admin or not.
     *
     * @param {UserState} state User state
     * @return {boolean} Flag, whether the logged in user is a admin or not
     */
    isAdmin(state: UserState): boolean {
        const current = state.current;
        if (!current) return false;

        return current.admin;
    },

    /**
     * Checks whether the user has a Discord account linked to it.
     *
     * @param {UserState} state User state
     * @return {boolean} Flag, whether the user has a Discord account linked to it
     */
    isDiscordLinked(state: UserState): boolean {
        return !!state.current?.discord;
    },

    /**
     * Checks whether the user has a Twitter account linked to it.
     *
     * @param {UserState} state User state
     * @return {boolean} Flag, whether the user has a Twitter account linked to it
     */
    isTwitterLinked(state: UserState): boolean {
        return !!state.current?.twitter;
    },

    /**
     * Returns the Discord username if the user has a Discord account linked.
     *
     * @param {UserState} state User state
     * @return {(string | null)} Discord username
     */
    discordUsername(state: UserState): string | null {
        if (!state.token) return null;
        if (!state.current?.discord) return null;

        const name = state.current?.discord?.username;
        const discriminator = state.current?.discord?.discriminator;
        return `${name}#${discriminator}`;
    },

    /**
     * Returns the Twitter handle if the user has a Twitter account linked.
     *
     * @param {UserState} state User state
     * @return {(string | null)} Twitter handle
     */
    twitterHandle(state: UserState): string | null {
        if (!state.token) return null;
        if (!state.current?.twitter) return null;

        return `@${state.current?.twitter?.username}`;
    },

    /**
     * Returns the snapshots of the user.
     *
     * @param {UserState} state User state
     * @return {SnapshotPlacement[]} Snapshots of the user
     */
    snapshots(state: UserState): SnapshotPlacement[] {
        if (!state.token) return [];
        if (!state.current) return [];
        if (!state.current.snapshots) return [];

        return state.current.snapshots;
    },
});

export default getters;
